import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import useRedux from "src/redux/useRedux";

import {
  Card,
  Stack,
  Button,
  Grid,
  CircularProgress,
  Box,
} from "@mui/material";
import TextField from "src/components/Inputs/TextField";
import { NormalText } from "src/components/styled/text.styled";
import MerchantLayout from "src/components/Layouts/MerchantLayout";
import { timeTypes, valueText, voucherTypes } from "src/constants/general";
import Dropdown from "src/components/Inputs/Dropdown";
import CurrencyFormat from "react-currency-format";
import { createVoucher } from "src/redux/voucher";
import { toast } from "react-toastify";
import Editor from "src/components/Editor";
import InputDateTime from "src/components/Inputs/DateTimePicker";
import { useVoucherForm } from "src/hooks/useVoucherForm";

const CreateVoucherPage = () => {
  const navigate = useNavigate();
  const {
    payload,
    setPayload,
    text,
    setText,
    errors,
    handleSubmitCreate,
    loading,
  } = useVoucherForm();
  const allowedKeyRegex = /^[a-zA-Z0-9]$/;

  const handleKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
    const key = event.key;

    const allowedKeys = [
      'Backspace', 'Delete', 'Tab', 'ArrowLeft', 'ArrowRight', 'ArrowUp', 'ArrowDown'
    ];

    // Cegah space dan karakter non-alfanumerik
    if (!allowedKeyRegex.test(key) && !allowedKeys.includes(key)) {
      event.preventDefault();
    }
  };

  return (
    <MerchantLayout>
      <Card sx={{ padding: "2%" }}>
        <NormalText fontSize="20px" fontWeight="bold" margin="0 0 20px 0">
          Tambah Voucher
        </NormalText>

        <TextField
          label="Nama Voucher"
          isRequired
          validationText={errors.name}
          validationType={errors.name ? "ERROR" : ""}
          value={payload.name}
          onChange={(e: any) => setPayload({ ...payload, name: e.target.value })}
        />

        <TextField
          label="Deskripsi"
          isRequired
          validationText={errors.description}
          validationType={errors.description ? "ERROR" : ""}
          value={payload.description}
          onChange={(e: any) => {
            setPayload({ ...payload, description: e.target.value })
          }}
        />

        <TextField
          label="Kode Voucher"
          isRequired
          validationText={errors.code}
          validationType={errors.code ? "ERROR" : ""}
          value={payload.code}
          onKeyDown={handleKeyDown}
          onChange={(e: any) => setPayload({ ...payload, code: e.target.value })}
        />

        <CurrencyFormat
          label="Harga Voucher"
          validationText={errors.purchasePrice}
          validationType={errors.purchasePrice ? "ERROR" : ""}
          customInput={TextField}
          thousandSeparator={true}
          value={payload.purchasePrice}
          onValueChange={(e: any) => setPayload({ ...payload, purchasePrice: e.value })}
        />

        <Grid container spacing={2}>
          <Grid item xs={12} sm={6} md={6}>
            <Dropdown
              label="Tipe Nilai Voucher"
              options={valueText}
              isRequired
              value={payload.valueText}
              validationText={errors.valueText}
              validationType={errors.valueText ? "ERROR" : ""}
              onChange={(e: any) => setPayload({ ...payload, valueText: e })}
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <CurrencyFormat
              label="Nilai Voucher"
              isRequired
              disabled={!payload.valueText}
              validationText={errors.value}
              validationType={errors.value ? "ERROR" : ""}
              customInput={TextField}
              thousandSeparator={true}
              value={payload.value}
              onValueChange={(e: any) => setPayload({ ...payload, value: e.value })}
            />
          </Grid>
        </Grid>

        <Grid container spacing={2}>
          <Grid item xs={12} sm={6} md={6}>
            <Dropdown
              label="Tipe Voucher"
              options={voucherTypes}
              isRequired
              value={payload.voucherType}
              validationText={errors.voucherType}
              validationType={errors.voucherType ? "ERROR" : ""}
              onChange={(e: any) => setPayload({ ...payload, voucherType: e })}
            />
          </Grid>
          <Grid item xs={12} sm={6} md={6}>
            <TextField
              label="Batas Maksimal Penggunaan"
              type="number"
              isRequired
              value={payload.maxLimit}
              validationText={errors.maxLimit}
              validationType={errors.maxLimit ? "ERROR" : ""}
              onChange={(e: any) => setPayload({ ...payload, maxLimit: e.target.value })}
            />
          </Grid>
        </Grid>

        <Grid container spacing={2}>
          <Grid item xs={12} sm={6}>
            <Dropdown
              label="Tipe Waktu Voucher"
              options={timeTypes}
              isRequired
              value={payload.timeType}
              validationText={errors.timeType}
              validationType={errors.timeType ? "ERROR" : ""}
              onChange={(e: any) => {
                const updated = { 
                  ...payload, 
                  startDate: payload.startDate, 
                  endDate: payload.endDate, 
                  expiryDay: payload.expiryDay, 
                  timeType: e 
                };

                if (e === "period") {
                  updated.expiryDay = "";
                } else {
                  updated.startDate = "";
                  updated.endDate = "";
                }
                setPayload(updated);
              }}
            />
          </Grid>

          {payload.timeType === "expire_date" && (
            <Grid item xs={12} sm={6}>
              <TextField
                label="Masa Berlaku (hari)"
                isRequired
                value={payload.expiryDay}
                validationText={errors.expiryDay}
                validationType={errors.expiryDay ? "ERROR" : ""}
                onChange={(e: any) => setPayload({ ...payload, expiryDay: e.target.value })}
              />
            </Grid>
          )}

          {payload.timeType === "period" && (
            <>
              <Grid item xs={12} sm={2}>
                <NormalText fontSize="12px" margin="0 0 10px 0">
                  Start Date *
                </NormalText>
                <InputDateTime
                  isRequired
                  value={payload.startDate}
                  disablePast
                  validationText={errors.startDate}
                  validationType={errors.startDate ? "ERROR" : ""}
                  inputFormat="dd/MM/yyyy HH:mm"
                  onChange={(value: any) => setPayload({ ...payload, startDate: value })}
                />
              </Grid>
              <Grid item xs={12} sm={2}>
                <NormalText fontSize="12px" margin="0 0 10px 0">
                  End Date *
                </NormalText>
                <InputDateTime
                  isRequired
                  value={payload.endDate}
                  minDate={payload.startDate ? new Date(payload.startDate) : undefined}
                  validationText={errors.endDate}
                  validationType={errors.endDate ? "ERROR" : ""}
                  inputFormat="dd/MM/yyyy HH:mm"
                  onChange={(value: any) => setPayload({ ...payload, endDate: value })}
                />
              </Grid>
            </>
          )}
        </Grid>

        <Box mt={2}>
          <NormalText fontSize="12px" margin="0 0 10px 0">
            Cara Penggunaan *
          </NormalText>
          <Editor value={text} onChange={setText} />
          {errors.text && (
            <NormalText
              color={"#f12147"}
              fontSize="12px"
              fontStyle="normal"
              fontWeight="400"
              margin={"5px 0 5px 0"}
            >
              {errors.text}
            </NormalText>
          )}
        </Box>

        <Stack direction={"row"} spacing={2} margin={"20px 0 20px 0"}>
          <Button
            sx={{
              textTransform: "none",
              background: "#e56c75",
              color: "#ffffff",
              "&:hover": {
                backgroundColor: "#e56c75",
                borderColor: "#e56c75",
                boxShadow: "none",
              },
            }}
            onClick={() => navigate("/products/vouchers")}
            className="px-4"
          >
            Batal
          </Button>
          <Button
            sx={{
              textTransform: "none",
              background: "#007bff",
              color: "#ffffff",
              "&:hover": {
                backgroundColor: "#007bff",
                borderColor: "#007bff",
                boxShadow: "none",
              },
            }}
            className="px-4"
            onClick={() => {console.log("clickkk"); handleSubmitCreate()}}
            disabled={loading}
          >
            {loading ? <CircularProgress size={15} className="mr-2"/> : "Simpan"}
          </Button>
        </Stack>
      </Card>
    </MerchantLayout>
  );
};

export default CreateVoucherPage;
